import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormDialog, Icon, InputField, MuiBox, SelectDropdown, Tooltip, Typography } from '@kis-technologies/kis-ui';
import { ManageIntegrationsOverlay } from 'components/ManageIntegrations';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { actions as integrationsActions, selectors as integrationsSelectors, Integration } from 'store/integrations';
import { doesFormHaveError, onInputBlur, onValueChange } from 'utils/forms';
import { getSortedList } from 'utils/stringFormat';
import { CreateEnvironmentForm, CreateEnvironmentOverlayProps } from '../types';
import { environmentValidationSchema, getCreateEnvironmentForm } from '../utils';

const CreateEnvironmentOverlay = ({ onClose, onSubmit }: CreateEnvironmentOverlayProps) => {
  const { getIntegrations } = useActions({ getIntegrations: integrationsActions.getIntegrations });
  const integrations = useSelector(integrationsSelectors.selectIntegrations);
  const sortedIntegrations = useMemo(() => getSortedList(integrations, 'name'), [integrations]);
  const admin = useSelector(authSelectors.selectAdmin);
  // Integrations are shared across clients, so only global admins can manage them
  const isGlobalAdmin = admin?.role === 'GlobalAdmin';

  const initialForm = useMemo(() => getCreateEnvironmentForm(), []);
  const [form, setForm] = useState<CreateEnvironmentForm>(initialForm);
  const [showManageIntegrations, setShowManageIntegrations] = useState(false);

  const isSaveDisabled = useMemo(
    () => !form.name.value.trim() || !form.integrationId.value || !form.baseUrl.value.trim() || doesFormHaveError(form),
    [form]
  );

  useEffect(() => {
    getIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the selection if the selected integration was deleted from Manage Integrations
  useEffect(() => {
    if (form.integrationId.value && !integrations.some((i) => i.id === form.integrationId.value)) {
      onValueChange('', 'integrationId', setForm);
    }
  }, [integrations]);

  return (
    <FormDialog
      open
      title="Create Environment"
      confirmButtonText="Save"
      confirmButtonAction={() =>
        onSubmit({
          name: form.name.value.trim(),
          integrationId: form.integrationId.value,
          baseUrl: form.baseUrl.value.trim()
        })
      }
      confirmButtonDisabled={isSaveDisabled}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
    >
      <MuiBox py="16px" width="524px" display="flex" flexDirection="column" gap="32px">
        {/* Each field is wrapped so the gap only applies between fields, not between a label and its input */}
        <MuiBox>
          <InputField
            autoFocus
            data-testid="environment-name-input"
            error={form.name.error !== null}
            fullWidth
            helperText={form.name.error}
            inputProps={{ maxLength: 255 }}
            label="Name"
            onBlur={(e) => onInputBlur(e, 'name', environmentValidationSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'name', setForm)}
            size="small"
            value={form.name.value}
          />
        </MuiBox>
        <MuiBox>
          <InputField
            data-testid="environment-url-input"
            error={form.baseUrl.error !== null}
            fullWidth
            helperText={form.baseUrl.error}
            inputProps={{ maxLength: 255 }}
            label="URL"
            onBlur={(e) => onInputBlur(e, 'baseUrl', environmentValidationSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'baseUrl', setForm)}
            size="small"
            value={form.baseUrl.value}
          />
        </MuiBox>
        <MuiBox>
          {/* Matches the InputField label style, with the gear above the dropdown's right edge like the kiosk form */}
          <MuiBox display="flex" justifyContent="space-between" alignItems="center" width="262px" mb="10px">
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#2A2A2A', padding: '0 16px' }}>
              Integration
            </Typography>
            {isGlobalAdmin && (
              <MuiBox mr="10px" display="flex">
                <Tooltip title="Manage Integrations" arrow placement="top" verticalOffset={-8}>
                  <Icon
                    name="Settings"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowManageIntegrations(true)}
                    data-testid="manage-integrations-button"
                  />
                </Tooltip>
              </MuiBox>
            )}
          </MuiBox>
          {/* Same size as the Environment dropdown on the Environments page */}
          <SelectDropdown
            items={sortedIntegrations}
            itemToString={(item: Integration) => (item ? item.name : '')}
            selectedItem={sortedIntegrations.find((i) => i.id === form.integrationId.value) || null}
            onChange={(item: Integration) => onValueChange(item.id, 'integrationId', setForm)}
            placeholder="Select Integration"
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: form.integrationId.value ? '#000' : '#9E9E9E'
              }
            }}
            data-testid="environment-integration-selector"
          />
        </MuiBox>
      </MuiBox>
      {showManageIntegrations && <ManageIntegrationsOverlay onClose={() => setShowManageIntegrations(false)} />}
    </FormDialog>
  );
};

export default CreateEnvironmentOverlay;
